/* eslint-disable import/no-unresolved */
import React, { useCallback, useMemo, useState } from 'react';

import RightModalLayout from '../../../../../../../../layouts/Modal/RightModalLayout';
import DropdownMC from '../../../../../../../../components/DropdownMC';
import {
  HTTP_METHODS,
  BODY_MODES,
  emptyBodyRow,
  emptyHeaderRow,
  emptyQueryParamRow
} from '../../hooks/globalInstructionToolsModal/retailerApiRequestPayload';
import RetailerAuthMethodsSection from './RetailerAuthMethodsSection';
import RetailerApiRequestRowsEditor from './RetailerApiRequestRowsEditor';

const floatingFieldClass =
  'tw-w-full tw-rounded-xl tw-border tw-border-gray-200 tw-bg-[#F7F8FD] tw-px-3 tw-py-3 tw-text-sm tw-text-gray-800 tw-outline-none focus:tw-border-gray-300';

const HEADER_LABELS = {
  sectionLabel: 'tools.retailer_api_request_headers_label',
  itemLabel: 'tools.retailer_api_request_header_item',
  remove: 'tools.retailer_api_request_header_remove',
  nameLabel: 'tools.retailer_api_request_header_name_label',
  namePlaceholder: 'tools.retailer_api_request_header_name_placeholder',
  valueLabel: 'tools.retailer_api_request_header_value_label',
  valuePlaceholder: 'tools.retailer_api_request_header_value_placeholder',
  descriptionLabel: 'tools.retailer_api_request_header_description_label',
  descriptionPlaceholder: 'tools.retailer_api_request_header_description_placeholder',
  descriptionHint: 'tools.retailer_api_request_header_description_hint',
  aiHint: 'tools.retailer_api_request_header_ai_hint',
  aiValuePlaceholder: 'tools.retailer_api_request_ai_value_placeholder',
  add: 'tools.retailer_api_request_header_add'
};

const QUERY_PARAM_LABELS = {
  sectionLabel: 'tools.retailer_api_request_query_params_label',
  itemLabel: 'tools.retailer_api_request_query_param_item',
  remove: 'tools.retailer_api_request_query_param_remove',
  nameLabel: 'tools.retailer_api_request_query_param_name_label',
  namePlaceholder: 'tools.retailer_api_request_query_param_name_placeholder',
  valueLabel: 'tools.retailer_api_request_query_param_value_label',
  valuePlaceholder: 'tools.retailer_api_request_query_param_value_placeholder',
  descriptionLabel: 'tools.retailer_api_request_query_param_description_label',
  descriptionPlaceholder: 'tools.retailer_api_request_query_param_description_placeholder',
  descriptionHint: 'tools.retailer_api_request_query_param_description_hint',
  aiHint: 'tools.retailer_api_request_query_param_ai_hint',
  aiValuePlaceholder: 'tools.retailer_api_request_ai_value_placeholder',
  add: 'tools.retailer_api_request_query_param_add'
};

const BODY_LABELS = {
  sectionLabel: 'tools.retailer_api_request_body_label',
  itemLabel: 'tools.retailer_api_request_body_item',
  remove: 'tools.retailer_api_request_body_remove',
  nameLabel: 'tools.retailer_api_request_body_name_label',
  namePlaceholder: 'tools.retailer_api_request_body_name_placeholder',
  valueLabel: 'tools.retailer_api_request_body_value_label',
  valuePlaceholder: 'tools.retailer_api_request_body_value_placeholder',
  descriptionLabel: 'tools.retailer_api_request_body_description_label',
  descriptionPlaceholder: 'tools.retailer_api_request_body_description_placeholder',
  descriptionHint: 'tools.retailer_api_request_body_description_hint',
  aiHint: 'tools.retailer_api_request_body_ai_hint',
  aiValuePlaceholder: 'tools.retailer_api_request_ai_value_placeholder',
  add: 'tools.retailer_api_request_body_add'
};

const formatTestResponse = (response) => {
  if (response == null || response === '') return '';
  if (typeof response === 'string') return response;

  try {
    return JSON.stringify(response, null, 2);
  } catch (_e) {
    return String(response);
  }
};

const RetailerApiRequestToolView = ({
  t,
  footerSubmitLabel,
  handleBack,
  apiRequestTool
}) => {
  const [saving, setSaving] = useState(false);
  const {
    handleConfirm,
    handleTest,
    testing,
    testResult,
    apiRequestDraft,
    patchDraft,
    authMethodsDraft,
    setAuthMethodsDraft,
    selectedAuthMethodId,
    setSelectedAuthMethodId,
    authMethodsFormMode,
    setAuthMethodsFormMode
  } = apiRequestTool;

  const methodOptions = useMemo(
    () => HTTP_METHODS.map((method) => ({ id: method, label: method })),
    []
  );

  const bodyModeOptions = useMemo(
    () => [
      { id: BODY_MODES.PARAMS, label: t('tools.retailer_api_request_body_mode_params') },
      { id: BODY_MODES.JSON, label: t('tools.retailer_api_request_body_mode_json') }
    ],
    [t]
  );

  const usesParamsBody = apiRequestDraft.bodyMode === BODY_MODES.PARAMS;
  const usesJsonBody = apiRequestDraft.bodyMode === BODY_MODES.JSON;

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    try {
      await handleConfirm();
    } finally {
      setSaving(false);
    }
  }, [handleConfirm]);

  return (
    <RightModalLayout
      closeAction={handleBack}
      title={t('tools.retailer_api_request_title')}
      showBackButton
      submitLabel={footerSubmitLabel}
      submitAction={handleSubmit}
      submitDisabled={saving}
      cancelLabel={t('Common:actions.cancel')}
      cancelAction={handleBack}
      footerVariant="VERTICAL"
    >
      <div className="tw-flex tw-flex-col tw-gap-5">
        <p className="tw-text-sm tw-text-gray-600">
          {t('tools.retailer_api_request_form_description')}
        </p>

        <div className="tw-rounded-xl tw-border tw-border-gray-100 tw-bg-white tw-p-4 tw-flex tw-flex-col tw-gap-4">
          <h3 className="tw-text-sm tw-font-semibold tw-text-gray-900">
            {t('tools.retailer_api_request_create_title')}
          </h3>

          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-xs tw-font-medium tw-text-gray-600" htmlFor="retailer_api_request_url">
              {t('tools.retailer_api_request_url_label')}
            </label>
            <input
              id="retailer_api_request_url"
              type="url"
              className={floatingFieldClass}
              value={apiRequestDraft.requestUrl}
              onChange={(e) => patchDraft({ requestUrl: e.target.value })}
              placeholder="https://"
            />
          </div>

          <DropdownMC
            id="retailer_api_request_method"
            name="retailer_api_request_method"
            label={t('tools.retailer_api_request_method_label')}
            options={methodOptions}
            onSelect={(httpMethod) => patchDraft({ httpMethod })}
            defaultValue={apiRequestDraft.httpMethod}
            useFloatingLabel
          />

          <RetailerAuthMethodsSection
            t={t}
            embedded
            selectedAuthMethodId={selectedAuthMethodId}
            setSelectedAuthMethodId={setSelectedAuthMethodId}
            retailerAuthMethodsDraft={authMethodsDraft}
            setRetailerAuthMethodsDraft={setAuthMethodsDraft}
            authMethodsFormMode={authMethodsFormMode}
            setAuthMethodsFormMode={setAuthMethodsFormMode}
          />

          <div className="tw-flex tw-flex-col tw-gap-3 tw-pt-1 tw-border-t tw-border-gray-100">
            <RetailerApiRequestRowsEditor
              t={t}
              rows={apiRequestDraft.headerRows || []}
              onChange={(headerRows) => patchDraft({ headerRows })}
              emptyRow={emptyHeaderRow}
              idPrefix="retailer_api_header"
              labels={HEADER_LABELS}
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-3 tw-pt-1 tw-border-t tw-border-gray-100">
            <RetailerApiRequestRowsEditor
              t={t}
              rows={apiRequestDraft.queryParamRows || []}
              onChange={(queryParamRows) => patchDraft({ queryParamRows })}
              emptyRow={emptyQueryParamRow}
              idPrefix="retailer_api_query_param"
              labels={QUERY_PARAM_LABELS}
            />
          </div>

          <div className="tw-flex tw-flex-col tw-gap-3 tw-pt-1 tw-border-t tw-border-gray-100">
            <span className="tw-text-xs tw-font-semibold tw-text-gray-700">
              {t('tools.retailer_api_request_body_label')}
            </span>

            <DropdownMC
              id="retailer_api_request_body_mode"
              name="retailer_api_request_body_mode"
              label={t('tools.retailer_api_request_body_mode_label')}
              options={bodyModeOptions}
              onSelect={(bodyMode) => patchDraft({ bodyMode })}
              defaultValue={apiRequestDraft.bodyMode}
              useFloatingLabel
            />

            {usesParamsBody && (
              <RetailerApiRequestRowsEditor
                t={t}
                rows={apiRequestDraft.bodyRows || []}
                onChange={(bodyRows) => patchDraft({ bodyRows })}
                emptyRow={emptyBodyRow}
                idPrefix="retailer_api_body"
                labels={BODY_LABELS}
                hideSectionLabel
              />
            )}

            {usesJsonBody && (
              <div className="tw-flex tw-flex-col tw-gap-1">
                <label className="tw-text-xs tw-font-medium tw-text-gray-600" htmlFor="retailer_api_request_body_json">
                  {t('tools.retailer_api_request_body_json_label')}
                </label>
                <textarea
                  id="retailer_api_request_body_json"
                  className={`${floatingFieldClass} tw-min-h-[160px] tw-resize-y tw-font-mono tw-text-xs`}
                  value={apiRequestDraft.bodyJsonContent || ''}
                  onChange={(e) => patchDraft({ bodyJsonContent: e.target.value })}
                  placeholder={t('tools.retailer_api_request_body_json_placeholder')}
                  spellCheck={false}
                />
                <p className="tw-text-[10px] tw-text-gray-400">
                  {t('tools.retailer_api_request_body_json_hint')}
                </p>
              </div>
            )}
          </div>

          <div className="tw-flex tw-flex-col tw-gap-3 tw-pt-1 tw-border-t tw-border-gray-100">
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
              <div>
                <h4 className="tw-text-xs tw-font-semibold tw-text-gray-700">
                  {t('tools.retailer_api_request_test_title')}
                </h4>
                <p className="tw-text-[11px] tw-text-gray-500 tw-mt-1">
                  {t('tools.retailer_api_request_test_description')}
                </p>
              </div>
              <button
                type="button"
                className="tw-shrink-0 tw-rounded-xl tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-blue-600 disabled:tw-opacity-50"
                onClick={handleTest}
                disabled={testing}
              >
                {testing ? t('tools.retailer_api_request_test_running') : t('tools.retailer_api_request_test_button')}
              </button>
            </div>

            {testResult && (
              <div
                className={`tw-rounded-xl tw-border tw-p-3 tw-flex tw-flex-col tw-gap-2 ${
                  testResult.success
                    ? 'tw-border-green-200 tw-bg-green-50'
                    : 'tw-border-red-200 tw-bg-red-50'
                }`}
              >
                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                  <span
                    className={`tw-text-xs tw-font-semibold ${
                      testResult.success ? 'tw-text-green-700' : 'tw-text-red-700'
                    }`}
                  >
                    {testResult.success
                      ? t('tools.retailer_api_request_test_success')
                      : t('tools.retailer_api_request_test_error')}
                  </span>
                  {testResult.status_code != null && (
                    <span className="tw-text-[11px] tw-font-medium tw-text-gray-600">
                      {t('tools.retailer_api_request_test_status_code', { code: testResult.status_code })}
                    </span>
                  )}
                </div>

                {testResult.message && (
                  <p className="tw-text-[11px] tw-text-gray-600">{testResult.message}</p>
                )}

                {testResult.response != null && testResult.response !== '' && (
                  <pre className="tw-max-h-48 tw-overflow-auto tw-rounded-lg tw-bg-white/70 tw-p-2 tw-text-[10px] tw-font-mono tw-text-gray-700 tw-whitespace-pre-wrap">
                    {formatTestResponse(testResult.response)}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </RightModalLayout>
  );
};

export default RetailerApiRequestToolView;
